import axios from 'axios';
import errorHandlers from '@/services/error-handlers';
import events from '@/services/events';
import returnAppConfig from '@/helpers/return-app-config';
import sharedPromise from '@/helpers/shared-promise';
import storage from '@/services/storage';
import { v4 as uuid } from 'uuid';
import uaParser from 'ua-parser-js';

const http = axios.create({ timeout: 70000 });



export function setDefaultCommonHeaders() {
  /**
   * Adds global custom headers to all requests
   * https://axios-http.com/docs/config_defaults
  */
  const appVersion = process.env.PACKAGE_VERSION;
  const { os, device } = uaParser(window.navigator.userAgent);
  const { timeZone } = window.Intl.DateTimeFormat().resolvedOptions();
  const { merchantId } = returnAppConfig();

  let deviceId = storage.local.get('deviceId');

  if (!deviceId) {
    deviceId = uuid();
    storage.local.set('deviceId', deviceId);
  }

  http.defaults.headers.common = {
    'device-id': deviceId,
    'device-model': device.model,
    'device-os': os.name,
    'device-os-version': os.version,
    'merchant-token-id': merchantId,
    'time-zone': timeZone,
    'cf-app-version': appVersion
  };
}

export function requestInterceptor(config) {
  const isCardFreeRequest = [
    '/cardfree-api/',
    '/auth-api/'
  ].some(path => config.url.includes(path));

  if (isCardFreeRequest) {
    /* Correlation ID */
    config.headers['x-correlation-id'] = uuid();

    /* UTM Source */
    const { utmSource } = storage.session.get('route') || {};
    if (utmSource) config.headers['utm-source'] = utmSource;
  }
  else {
    /**
    * Removes global custom headers for any
    * requests to non-CardFree services
    */
    config.headers.common = {};
  }

  return config;
}

export async function responseErrorInterceptor(error) {
  const { config, response } = error;
  const isAuthEndpoint = config.url.indexOf('/auth-api/') === 0;

  if (response && response.status === 401 && !isAuthEndpoint) {
    error.unauthorized = true;

    try {
      /**
      * NOTE: The sharedPromise() helper makes sure we only have a single
      * refresh HTTP request running at any one time incase we receive multiple
      * 401's at the exact same time. Without this, we'd get stuck in an ugly
      * loop where we refresh tokens, invalidate previous tokens, receive another
      * 401, then refresh tokens again, and repeat.
      */

      await sharedPromise('http:authRefresh', () => http.post('/auth-api/refresh'));

      return http.request(config);
    }

    catch (refreshError) {
      errorHandlers.silent(refreshError);
    }

    events.emit('unauthorized');
  }

  return Promise.reject(error);
}



http.interceptors.request.use(requestInterceptor);

http.interceptors.response.use(
  undefined,
  responseErrorInterceptor
);

setDefaultCommonHeaders();

export default http;
